// src/pages/Repositories.js
import { useState, useEffect } from 'react'
import { supabase } from '../utils/supabase'
import Layout from '../components/common/Layout'
import {
    ServerIcon,
    PlusIcon,
    Cog6ToothIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    SparklesIcon,
    ArrowTopRightOnSquareIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline'
import {Link, useNavigate} from "react-router-dom";

// Toggle Switch Component
function Toggle({ enabled, onChange, disabled = false }) {
    return (
        <button
            type="button"
            disabled={disabled}
            className={`${
                enabled ? 'bg-blue-600' : 'bg-gray-200'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} 
      relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent 
      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 
      focus:ring-blue-600 focus:ring-offset-2`}
            role="switch"
            aria-checked={enabled}
            onClick={() => !disabled && onChange(!enabled)}
        >
      <span
          aria-hidden="true"
          className={`${
              enabled ? 'translate-x-4' : 'translate-x-0'
          } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white 
        shadow ring-0 transition duration-200 ease-in-out`}
      />
        </button>
    )
}

// Plan Badge Component
function PlanBadge({ plan }) {
    const isPro = plan === 'pro'
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isPro
                ? 'bg-purple-100 text-purple-800'
                : 'bg-gray-100 text-gray-800'
        }`}>
      {isPro ? (
          <>
              <SparklesIcon className="w-3 h-3 mr-1" />
              Pro
          </>
      ) : (
          'Free'
      )}
    </span>
    )
}

// Usage Progress Bar Component
function UsageProgress({ current, limit }) {
    const percentage = Math.min((current / limit) * 100, 100)
    const isNearLimit = percentage >= 80
    const isOverLimit = percentage >= 100

    return (
        <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex-1 min-w-0">
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                            isOverLimit ? 'bg-red-500' : isNearLimit ? 'bg-orange-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                </div>
            </div>
            <div className="flex items-center gap-1 text-sm font-medium min-w-0">
        <span className={`${
            isOverLimit ? 'text-red-600' : isNearLimit ? 'text-orange-600' : 'text-gray-900'
        }`}>
          {current.toLocaleString()}
        </span>
                <span className="text-gray-500">/</span>
                <span className="text-gray-500">{limit.toLocaleString()}</span>
                {isOverLimit && (
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 ml-1" />
                )}
            </div>
        </div>
    )
}

// Handle connect new repository
export const handleConnectRepository = () => {
    // Redirect to GitHub App installation
    const githubAppUrl = `https://github.com/apps/echoguard-app/installations/new`
    window.open(githubAppUrl, '_blank', 'noopener,noreferrer')
}

export default function Repositories() {
    const [loading, setLoading] = useState(true)
    const [repositories, setRepositories] = useState([])
    const [repositoryUsage, setRepositoryUsage] = useState({})
    const navigate = useNavigate()

    // Calculate current month's usage for each repository
    const calculateMonthlyUsage = async () => {
        try {
            const now = new Date()
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
            const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)

            const { data: usage, error } = await supabase
                .from('similar_issue_links')
                .select('repo_id')
                .gte('created_at', startOfMonth.toISOString())
                .lt('created_at', endOfMonth.toISOString())

            if (error) throw error

            // 在客户端计算每个仓库的使用量
            const repositoryUsage = {}
            usage?.forEach(record => {
                repositoryUsage[record.repo_id] = (repositoryUsage[record.repo_id] || 0) + 1
            })

            setRepositoryUsage(repositoryUsage)
        } catch (error) {
            console.error('Error calculating usage:', error)
        }
    }


    // Fetch repositories
    const fetchRepositories = async () => {
        try {
            setLoading(true)

            const { data, error } = await supabase
                .from('repositories')
                .select('*')
                .order('created_at', { ascending: false })

            if (error) throw error
            console.log(data)
            setRepositories(data || [])
            await calculateMonthlyUsage()
        } catch (error) {
            console.error('Error fetching repositories:', error)
        } finally {
            setLoading(false)
        }
    }

    // Handle repository toggle
    const handleToggleRepo = async (repoId, isActive) => {
        try {
            const { error } = await supabase
                .from('repositories')
                .update({ is_active: isActive })
                .eq('id', repoId)

            if (error) throw error

            setRepositories(prev =>
                prev.map(repo =>
                    repo.id === repoId ? { ...repo, is_active: isActive } : repo
                )
            )
        } catch (error) {
            console.error('Error toggling repository:', error)
        }
    }
    // Handle upgrade to pro
    const handleUpgrade = (repoId) => {
        console.log('Upgrade repository to pro:', repoId)
        // Navigate to upgrade flow
        // Example: navigate(`/repositories/${repoId}/upgrade`)
    }

    useEffect(() => {
        fetchRepositories()
    }, [])

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        )
    }

    return (
        <Layout>
            <div className="space-y-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Repositories</h1>
                        <p className="mt-2 text-gray-600">
                            Manage your connected repositories and monitor usage limits
                        </p>
                    </div>
                    <button
                        onClick={handleConnectRepository}
                        className="mt-4 sm:mt-0 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Connect Repository
                    </button>
                </div>

                {/* Usage Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                    <div className="flex items-start gap-3">
                        <InformationCircleIcon className="w-6 h-6 text-blue-600 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-blue-900 mb-2">Usage Limits & Plan Details</h3>
                            <div className="text-sm text-blue-800 space-y-1">
                                <p><strong>Free Plan:</strong> 100 similar issue checks per month. The Free plan is only available for <strong>public repositories</strong> with under <strong>1,000 issues</strong>.</p>
                                <p><strong>Pro Plan:</strong> 500 similar issue checks per month, with support for private repositories.</p>
                                <p className="pt-1 text-blue-700">Each repository has its own plan and usage limit. Upgrade individual repositories as needed.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Alpha Testing Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-yellow-900 mb-2">Alpha Testing Period</h3>
                            <div className="text-sm text-yellow-800 space-y-1">
                                <p>During our alpha testing phase, all connected repositories automatically receive <strong>Pro plan features for free</strong>.</p>
                                <p className="pt-1 text-yellow-700">Please note that we reserve the right to take action against any misuse of the service during this period.</p>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Repositories List */}
                {repositories.length > 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Repository</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Plan</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Monthly Usage</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                {repositories.map((repository) => {
                                    const usage = repositoryUsage[repository.id] || 0
                                    const planLimit = repository.plan_id === 'pro' ? 500 : 100
                                    const isOverLimit = usage >= planLimit
                                    const canUpgrade = repository.plan_id === 'free'

                                    return (
                                        <tr key={repository.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    {/*<div className="p-2 bg-gray-100 rounded-lg">*/}
                                                    {/*    <ServerIcon className="w-4 h-4 text-gray-600" />*/}
                                                    {/*</div>*/}
                                                    <div>
                                                        <Link
                                                            to={`/repository/${repository.id}`}
                                                            className="font-medium text-gray-900 hover:text-blue-600 hover:underline transition-colors duration-200 cursor-pointer"
                                                        >
                                                            {repository.name.split('/').pop()}
                                                        </Link>
                                                        <a
                                                            href={`https://github.com/${repository.name}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mt-1"
                                                        >
                                                            View on GitHub
                                                            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <PlanBadge plan={repository.plan_id} />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className={`flex items-center gap-2 text-sm ${
                                                    repository.is_active ? 'text-green-600' : 'text-gray-500'
                                                }`}>
                                                    <div className={`w-2 h-2 rounded-full ${
                                                        repository.is_active ? 'bg-green-500' : 'bg-gray-400'
                                                    }`} />
                                                    {repository.is_active ? 'Active' : 'Inactive'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <UsageProgress current={usage} limit={planLimit} />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    {isOverLimit && canUpgrade && (
                                                        <button
                                                            onClick={() => handleUpgrade(repository.id)}
                                                            className="text-sm px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                                                        >
                                                            Upgrade to Pro
                                                        </button>
                                                    )}
                                                    <Toggle
                                                        enabled={repository.is_active}
                                                        onChange={(enabled) => handleToggleRepo(repository.id, enabled)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <ServerIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No repositories connected</h3>
                        <p className="text-gray-600 mb-6">
                            Connect your first GitHub repository to start automating issue management
                        </p>
                    </div>
                )}
            </div>
        </Layout>
    )
}
